/**
 * Shared TanStack Query client singleton.
 *
 * Extracted from `App.tsx` (Story — upload em segundo plano) so code OUTSIDE
 * the React tree can invalidate queries too: the background-upload store
 * (`@/stores/background-upload-store`) creates an evaluation from a `setTimeout`-
 * driven async flow that keeps running after the create-evaluation screen has
 * unmounted, and needs to tell the applications list / evaluations tab to
 * refetch once that finishes. A module-level singleton, imported by both
 * `App.tsx` and the store, is simpler than threading the client through
 * a non-component call site.
 *
 * A fábrica (`createQueryClient`) existe para os testes: cada um monta um cliente
 * com as MESMAS regras do da aplicação, sem partilhar a cache do singleton.
 */
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { fetchMe, type MeResponse } from '@/lib/api/me'

/** A chave da sessão — a mesma do `useQuery` do `hooks/use-auth.ts`. */
const SESSION_KEY = ['auth', 'me'] as const

/** Quanto a reconferência espera pelo `/api/me` antes de desistir (Story 26.1). */
const RECHECK_TIMEOUT_MS = 10_000

/** Depois de uma reconferência sem resposta, quanto tempo não se reconfere (Story 26.1). */
const RECHECK_PAUSE_MS = 30_000

/**
 * A SESSÃO QUE VENCE NO MEIO DO USO (Story 25.1, AC-5).
 *
 * Quando uma chamada falha, o cliente pergunta de novo ao `/api/me` se a sessão
 * ainda existe. Se não existir, semeia `{ user: null }`, e são as guardas
 * (`ProtectedRoute`/`PortalRoute`) que levam ao login — já com o `?next=` da tela
 * em que a pessoa estava. Nada aqui navega.
 *
 * Porquê não um interceptor de `fetch` que trate o 401: há 401 e 403 que NÃO são
 * sessão vencida — as rotas públicas por token (catálogo, convite, redefinição de
 * senha) e a senha errada no próprio login. E o status nem chega igual a toda a
 * parte: uns módulos de `lib/api/` lançam `ApiError` com `.status`, outros um
 * `Error` simples. Quem responde se a sessão morreu é o `/api/me`, e só ele.
 *
 * As três travas:
 *
 *   • só se reconfere quando a cache ACHAVA que havia sessão — é o que separa
 *     "venceu" de "nunca houve", e o que deixa as telas públicas e o login sem
 *     ida extra ao `/api/me`;
 *   • uma reconferência de cada vez: quando a sessão vence, as queries da tela
 *     falham todas no mesmo instante;
 *   • a própria query da sessão não dispara nada: reconferir o `/api/me` porque o
 *     `/api/me` falhou é perguntar duas vezes a mesma coisa.
 *
 * Uma falha do próprio `/api/me` na reconferência (rede, 500) não diz nada sobre a
 * sessão, e deixa tudo como estava.
 *
 * Numa queda da API (Story 26.1):
 *
 *   • a reconferência desiste ao fim de `RECHECK_TIMEOUT_MS` e solta a trava. Um
 *     `/api/me` pendurado prendia-a até o navegador desistir, e nesse intervalo
 *     nenhuma falha reconferia. Desistir conta como "sem resposta", e a resposta
 *     que chegar depois é ignorada, mesmo um 401. O pedido não é abortado: o
 *     `fetchMe` é a `queryFn` do `useAuth`, e um `signal` ali tornaria a query da
 *     sessão cancelável pelo TanStack;
 *   • uma reconferência sem resposta (rede, 5xx ou timeout) pausa as seguintes
 *     por `RECHECK_PAUSE_MS`, contados do instante em que ficou sem resposta. Sem
 *     isso, cada poll de 2 s que falha ia ao `/api/me`, que também falha.
 *
 * Uma resposta real não pausa nada. Viva, a falha seguinte reconfere logo: é ela
 * que leva ao login quem perde a sessão logo depois. Vencida, quem entra de novo
 * volta a ter a reconferência desde a primeira falha.
 */
export function createQueryClient(): QueryClient {
  let rechecking = false
  /** Até quando as reconferências estão em pausa (`Date.now()`); 0 = sem pausa. */
  let pausedUntil = 0

  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: (_error, query) => {
        if (query.queryKey[0] === SESSION_KEY[0] && query.queryKey[1] === SESSION_KEY[1]) return
        recheckSession()
      },
    }),
    mutationCache: new MutationCache({
      onError: () => {
        recheckSession()
      },
    }),
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  })

  function recheckSession(): void {
    if (rechecking) return
    if (Date.now() < pausedUntil) return
    if (!client.getQueryData<MeResponse>(SESSION_KEY)?.user) return

    rechecking = true
    // O marcador DESTA reconferência: o primeiro desfecho (resposta, rejeição ou
    // timeout) é o que vale, e o que chegar depois não faz nada.
    let pending = true
    const conclude = (outcome: () => void) => {
      if (!pending) return
      pending = false
      clearTimeout(timer)
      rechecking = false
      outcome()
    }
    // Sem resposta sobre a sessão: fica como estava, e pausa (ver acima).
    const noAnswer = () => {
      pausedUntil = Date.now() + RECHECK_PAUSE_MS
    }

    const timer = setTimeout(() => conclude(noAnswer), RECHECK_TIMEOUT_MS)
    fetchMe().then(
      (me) =>
        conclude(() => {
          if (!me.user) client.setQueryData<MeResponse>(SESSION_KEY, me)
        }),
      () => conclude(noAnswer),
    )
  }

  return client
}

export const queryClient = createQueryClient()
